import { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, TextField, MenuItem, Switch, FormControlLabel, Button, Collapse,
  Chip, Alert, Divider, Tabs, Tab, IconButton, Checkbox, ListItemText, useTheme, alpha,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { useSnackbar } from "notistack";
import { ACCENT } from "./accent";
import { useAuthGate } from "../../../hooks/useAuthGate";
import { DEMO_CATALOG, DEMO_DRONES } from "./demo/catalog";
import { listCalculatorMotors } from "../motorData/api";
import { simulateModeled, sweepModeled } from "./demo/model";
import ResultsView from "./ResultsView";
import SubmitPartDialog from "./SubmitPartDialog";
import ReportDataDialog from "./ReportDataDialog";
import ComparisonTray from "./ComparisonTray";
import {
  readStoredComparisons, loadComparisonSets, createComparisonSet, deleteComparisonSets,
  getComparisonSet, comparisonDate,
} from "./comparisonsStore";
import { APP_BASE, CLASSIC_PATH } from "./paths";
import { Link as RouterLink } from "react-router-dom";

// Shared-setup links: the whole config rides in a ?s= base64 payload, so a link
// reproduces the exact setup with no backend and no account.
const parseSharedSetup = () => {
  try {
    const s = new URLSearchParams(window.location.search).get("s");
    return s ? JSON.parse(atob(s)) : null;
  } catch {
    return null;
  }
};

// Pinned comparisons survive reloads via localStorage.
const PINS_KEY = "vcs-propulsion-pins";
const readStoredPins = () => {
  try {
    const raw = localStorage.getItem(PINS_KEY);
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.slice(0, 4) : [];
  } catch {
    return [];
  }
};

// Opened from the private list page: ?comparison=<id> deep-links straight into
// the Comparison tab with that saved set loaded.
const readComparisonParam = () => {
  try {
    const id = new URLSearchParams(window.location.search).get("comparison");
    if (!id) return null;
    return readStoredComparisons().find((c) => c.id === id) || null;
  } catch {
    return null;
  }
};

const motorLabel = (m) => `${m.brand} ${m.name}${m.kv ? ` · ${m.kv}Kv` : ""}`;
const propLabel = (p) => `${p.brand} ${p.name}${p.diameter_in ? ` · ${p.diameter_in}×${p.pitch_in}` : ""}`;
const escLabel = (e) => `${e.brand} ${e.name}${e.max_current_a ? ` · ${e.max_current_a}A` : ""}`;
const batLabel = (b) => `${b.brand} ${b.name}`;
const droneLabel = (d) => `${d.brand} · ${d.name}`;

// Unit conversions (canonical = base unit, first entry). to: base→display, from: display→base.
const UNIT_DEFS = {
  weight: [{ k: "g", label: "g", to: (v) => v, from: (v) => v }, { k: "oz", label: "oz", to: (v) => v * 0.035274, from: (v) => v / 0.035274 }],
  elev: [{ k: "m", label: "m", to: (v) => v, from: (v) => v }, { k: "ft", label: "ft", to: (v) => v * 3.28084, from: (v) => v / 3.28084 }],
  temp: [{ k: "C", label: "°C", to: (v) => v, from: (v) => v }, { k: "F", label: "°F", to: (v) => v * 1.8 + 32, from: (v) => (v - 32) / 1.8 }],
  press: [{ k: "hPa", label: "hPa", to: (v) => v, from: (v) => v }, { k: "inHg", label: "inHg", to: (v) => v * 0.02953, from: (v) => v / 0.02953 }],
  len: [{ k: "in", label: "in", to: (v) => v, from: (v) => v }, { k: "mm", label: "mm", to: (v) => v * 25.4, from: (v) => v / 25.4 }],
  lenmm: [{ k: "mm", label: "mm", to: (v) => v, from: (v) => v }, { k: "in", label: "in", to: (v) => v / 25.4, from: (v) => v * 25.4 }],
};

const round2 = (v) => Math.round(v * 100) / 100;

/** Numeric field with a unit toggle; value is always stored in the base unit. */
const UnitField = ({ label, value, onChange, defsKey, sx }) => {
  const defs = UNIT_DEFS[defsKey];
  const [u, setU] = useState(defs[0].k);
  const def = defs.find((d) => d.k === u);
  const shown = value === "" || value == null ? "" : round2(def.to(Number(value)));
  return (
    <Box sx={{ display: "flex", gap: 0.5, ...sx }}>
      <TextField
        size="small" type="number" label={label} value={shown}
        onChange={(e) => { const raw = e.target.value; onChange(raw === "" ? "" : def.from(Number(raw))); }}
        sx={{ flex: 1 }}
      />
      <TextField select size="small" value={u} onChange={(e) => setU(e.target.value)} sx={{ width: 74, flexShrink: 0 }}>
        {defs.map((d) => <MenuItem key={d.k} value={d.k}>{d.label}</MenuItem>)}
      </TextField>
    </Box>
  );
};

const NumField = ({ label, value, onChange, sx, ...rest }) => (
  <TextField size="small" type="number" label={label} value={value}
    onChange={(e) => onChange(e.target.value)} sx={sx} {...rest} />
);

const SpecToggle = ({ open, onClick, label = "Fine-tune specs" }) => (
  <Button
    size="small" onClick={onClick}
    endIcon={<ExpandMoreIcon sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />}
    sx={{ color: ACCENT, textTransform: "none", fontWeight: 600, alignSelf: "center", ml: "auto" }}
  >
    {open ? "Hide specs" : label}
  </Button>
);

const Section = ({ label, theme, primary, advanced, open, onToggle, toggleLabel }) => (
  <Box sx={{ borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}>
    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1, sm: 2 }, py: 2 }}>
      <Typography sx={{ width: { sm: 96 }, flexShrink: 0, fontWeight: 700, color: theme.palette.text.primary, pt: { sm: 1 } }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, flex: 1, alignItems: "flex-start" }}>
        {primary}
        {advanced && <SpecToggle open={open} onClick={onToggle} label={toggleLabel} />}
      </Box>
    </Box>
    {advanced && (
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: { sm: "112px" }, pb: 2, display: "flex", flexWrap: "wrap", gap: 1.5 }}>{advanced}</Box>
      </Collapse>
    )}
  </Box>
);

const DEFAULTS = {
  battery: { capacity_mah: 4000, resistance_ohm: 0.0035, nominal_voltage_v: 3.7, c_rating_max: 30 },
  esc: { resistance_ohm: 0.007, max_current_a: 40 },
  motor: { kv: 980, no_load_current_a: 0.5, no_load_voltage_v: 10, max_power_w: "", resistance_ohm: 0.09, max_current_a: 18, case_length_mm: "", poles: 14, weight_g: 56 },
  prop: { diameter_in: 10, pitch_in: 4.7, blades: 2, t_const: 0.95, p_const: 1.0 },
};

/**
 * Public Propulsion Calculator — eCalc-style, runs entirely in the browser.
 * Clean by default: pick from the catalog + a few key fields. The full spec
 * sheet (resistance, poles, constants…) lives behind each section's "Fine-tune".
 * Everything recomputes live; the verdict reads the result for the builder.
 */
const PublicPropulsion = () => {
  const theme = useTheme();
  const { isAuthenticated, requireSignin } = useAuthGate();

  // Motor list = the built-in demo entries plus every catalog motor the model
  // can actually solve (see listCalculatorMotors). Starts as the demo list so
  // the first paint has something to pick, then extends once the catalog
  // arrives; if that request fails the calculator simply keeps the demo list.
  const [catalogMotors, setCatalogMotors] = useState([]);
  useEffect(() => {
    let alive = true;
    listCalculatorMotors().then((rows) => alive && setCatalogMotors(rows));
    return () => {
      alive = false;
    };
  }, []);
  const motorOptions = useMemo(() => [...DEMO_CATALOG.motors, ...catalogMotors], [catalogMotors]);
  const { enqueueSnackbar } = useSnackbar();

  // A ?s= link restores the exact shared setup; otherwise defaults apply.
  const [shared] = useState(parseSharedSetup);

  const [droneId, setDroneId] = useState(shared?.droneId ?? "d-450");
  const [ids, setIds] = useState(
    shared?.ids ?? { motor: "m-x2212", propeller: "p-apc10", esc: "e-xrotor40", battery: "b-4000" }
  );
  const [open, setOpen] = useState({});
  const toggle = (k) => () => setOpen((s) => ({ ...s, [k]: !s[k] }));

  const [dialog, setDialog] = useState(null); // "submit" | "report" | null

  const [general, setGeneral] = useState(
    shared?.general ?? {
      all_up_weight_g: 1200, num_motors: 4, coaxial: false,
      accessory_current_a: 0,
      frame_size_mm: "", tilt_limit_deg: "",
    }
  );
  const [battery, setBattery] = useState(
    shared?.battery ?? {
      ...DEFAULTS.battery, cells_series: 3, parallel: 1, max_discharge_pct: 85,
      charge_state: "normal", c_rating_cont: 20, weight_g: "",
    }
  );
  const [esc, setEsc] = useState(
    shared?.esc ?? {
      ...DEFAULTS.esc, cont_current_a: "", weight_g: "",
      batt_wire_awg: 10, batt_wire_mm: 0, motor_wire_awg: 10, motor_wire_mm: 0,
    }
  );
  const [motor, setMotor] = useState(shared?.motor ?? { ...DEFAULTS.motor, cooling: "good" });
  const [prop, setProp] = useState(shared?.prop ?? { ...DEFAULTS.prop, gear_ratio: 1, yoke_twist_deg: 0 });

  // Avionics — flight controller + multi-select sensors (draw feeds bus current,
  // weight feeds drive weight, temp ranges are checked against the environment).
  const [fcu, setFcu] = useState(
    shared?.fcu ?? { id: "fc-pixhawk6c", power_draw_w: 3.0, weight_g: 35, temp_min_c: -20, temp_max_c: 60 }
  );
  const [sensorIds, setSensorIds] = useState(shared?.sensorIds ?? ["s-m9n"]);

  // Operating environment: thrust computed at the hot end (worst air density),
  // battery derated at the cold end.
  const [env, setEnv] = useState(
    shared?.env ?? { temp_min_c: 0, temp_max_c: 35, field_elevation_m: 0, pressure_hpa: 1013 }
  );
  const setEnvF = (k) => (v) => setEnv((s) => ({ ...s, [k]: v }));
  const setFc = (k) => (v) => setFcu((s) => ({ ...s, [k]: v }));

  const pickFcu = (e) => {
    const id = e.target.value;
    const item = DEMO_CATALOG.fcus.find((x) => x.id === id);
    setFcu(
      item
        ? { id, power_draw_w: item.power_draw_w, weight_g: item.weight_g, temp_min_c: item.temp_min_c, temp_max_c: item.temp_max_c }
        : { id: "", power_draw_w: 0, weight_g: 0, temp_min_c: "", temp_max_c: "" }
    );
  };

  const selectedSensors = sensorIds
    .map((id) => DEMO_CATALOG.sensors.find((s) => s.id === id))
    .filter(Boolean);
  const sensorsDrawW = selectedSensors.reduce((a, s) => a + (s.power_draw_w || 0), 0);
  const sensorsWeightG = selectedSensors.reduce((a, s) => a + (s.weight_g || 0), 0);

  // Pinned comparison snapshots. Anonymous users get 2 slots; sign in for more.
  // Priority: shared-link pins > ?comparison= deep link > locally stored pins.
  const [openedComp] = useState(readComparisonParam);
  const [pinned, setPinned] = useState(() =>
    Array.isArray(shared?.pinned)
      ? shared.pinned.slice(0, 4)
      : openedComp
      ? openedComp.entries.slice(0, 4)
      : readStoredPins()
  );
  const pinLimit = isAuthenticated ? 4 : 2;

  // Persist pins so a reload doesn't lose the comparison.
  useEffect(() => {
    try {
      localStorage.setItem(PINS_KEY, JSON.stringify(pinned));
    } catch {
      /* storage full/blocked — comparison still works for the session */
    }
  }, [pinned]);

  // Calculator / Comparison tabs + named saved comparison sets — account-synced
  // when signed in, this-browser otherwise. A ?comparison= deep link (from the
  // private list page) opens straight into the tab.
  const [tab, setTab] = useState(openedComp ? "compare" : "calc");
  const [savedComps, setSavedComps] = useState([]);
  const [compsSource, setCompsSource] = useState("local");
  const [compName, setCompName] = useState("");

  useEffect(() => {
    let live = true;
    loadComparisonSets(isAuthenticated).then(({ source, comparisons }) => {
      if (!live) return;
      setSavedComps(comparisons);
      setCompsSource(source);
    });
    return () => {
      live = false;
    };
  }, [isAuthenticated]);

  // Account-stored deep link (local ones are handled synchronously above).
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("comparison");
    if (!id || openedComp || !isAuthenticated) return;
    getComparisonSet(true, id).then((c) => {
      if (!c) return;
      setPinned((c.entries || []).slice(0, 4));
      setTab("compare");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveComparison = async () => {
    if (!pinned.length) return;
    const name = compName.trim() || `Comparison ${savedComps.length + 1}`;
    const { source, comparison } = await createComparisonSet(isAuthenticated, { name, entries: pinned });
    setSavedComps((s) => [...s, comparison]);
    setCompName("");
    enqueueSnackbar(
      source === "account"
        ? `Saved “${name}” to your account — available on all your devices.`
        : `Saved “${name}” in this browser${isAuthenticated ? "" : " — sign in to sync across devices"}.`,
      { variant: "success" }
    );
  };

  const deleteSavedComp = (id) => {
    deleteComparisonSets(compsSource, [id]);
    setSavedComps((s) => s.filter((x) => x.id !== id));
  };

  const loadComparison = (c) => {
    setPinned(c.entries.slice(0, 4));
    enqueueSnackbar(`Loaded “${c.name}”.`, { variant: "info" });
  };

  const shareSetup = async () => {
    const payload = btoa(JSON.stringify({ droneId, ids, general, battery, esc, motor, prop, fcu, sensorIds, env, pinned }));
    const url = `${window.location.origin}${window.location.pathname}?s=${payload}`;
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar("Setup link copied — anyone who opens it sees this exact configuration.", { variant: "success" });
    } catch {
      // Clipboard can be blocked (permissions/iframe) — show the link instead.
      window.prompt("Copy this setup link:", url);
    }
  };

  const setG = (k) => (v) => setGeneral((s) => ({ ...s, [k]: v }));
  const setB = (k) => (v) => setBattery((s) => ({ ...s, [k]: v }));
  const setE = (k) => (v) => setEsc((s) => ({ ...s, [k]: v }));
  const setM = (k) => (v) => setMotor((s) => ({ ...s, [k]: v }));
  const setP = (k) => (v) => setProp((s) => ({ ...s, [k]: v }));

  const pick = (kind) => (e) => {
    const id = e.target.value;
    setIds((s) => ({ ...s, [kind]: id }));
    const list = { motor: motorOptions, propeller: DEMO_CATALOG.propellers, esc: DEMO_CATALOG.escs, battery: DEMO_CATALOG.batteries }[kind];
    const item = list.find((x) => x.id === id);
    if (!item) return;
    if (kind === "battery") setBattery((s) => ({ ...s, capacity_mah: item.capacity_mah ?? s.capacity_mah, resistance_ohm: item.resistance_ohm ?? s.resistance_ohm, nominal_voltage_v: item.nominal_voltage_v ?? s.nominal_voltage_v, c_rating_max: item.c_rating_max ?? s.c_rating_max }));
    if (kind === "esc") setEsc((s) => ({ ...s, resistance_ohm: item.resistance_ohm ?? s.resistance_ohm, max_current_a: item.max_current_a ?? s.max_current_a }));
    if (kind === "motor") setMotor((s) => ({ ...s, kv: item.kv ?? s.kv, no_load_current_a: item.no_load_current_a ?? s.no_load_current_a, resistance_ohm: item.resistance_ohm ?? s.resistance_ohm, max_current_a: item.max_current_a ?? s.max_current_a, poles: item.poles ?? s.poles, weight_g: item.weight_g ?? s.weight_g }));
    if (kind === "propeller") setProp((s) => ({ ...s, diameter_in: item.diameter_in ?? s.diameter_in, pitch_in: item.pitch_in ?? s.pitch_in, blades: item.blades ?? s.blades, t_const: item.t_const ?? s.t_const, p_const: item.p_const ?? s.p_const }));
  };

  const applyDrone = (e) => {
    const id = e.target.value;
    setDroneId(id);
    const d = DEMO_DRONES.find((x) => x.id === id);
    if (!d) return;
    setGeneral((s) => ({ ...s, all_up_weight_g: d.auw_g ?? s.all_up_weight_g, num_motors: d.num_motors ?? s.num_motors, coaxial: d.coaxial ?? s.coaxial }));
    setBattery((s) => ({ ...s, cells_series: d.cells_series ?? s.cells_series }));
    if (d.motors?.[0]?.id) pick("motor")({ target: { value: d.motors[0].id } });
  };

  const num = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };

  // Charge state scales the per-cell voltage (LiPo: 4.20 full / nominal / ~3.50 low).
  const CHARGE_FACTOR = { full: 4.2 / 3.7, normal: 1, low: 3.5 / 3.7 };

  const { result, curve, error } = useMemo(() => {
    try {
      const parts = {
        motor: { kv: num(motor.kv, 1000), no_load_current_a: num(motor.no_load_current_a), resistance_ohm: num(motor.resistance_ohm, 0.05), max_current_a: num(motor.max_current_a) || undefined, max_power_w: num(motor.max_power_w) || undefined, weight_g: num(motor.weight_g) || undefined },
        propeller: { diameter_in: num(prop.diameter_in), pitch_in: num(prop.pitch_in), blades: num(prop.blades, 2), t_const: num(prop.t_const, 1), p_const: num(prop.p_const, 1) },
        esc: { resistance_ohm: num(esc.resistance_ohm), max_current_a: num(esc.max_current_a) || undefined, cont_current_a: num(esc.cont_current_a) || undefined },
        battery: {
          capacity_mah: num(battery.capacity_mah), resistance_ohm: num(battery.resistance_ohm),
          nominal_voltage_v: num(battery.nominal_voltage_v, 3.7) * (CHARGE_FACTOR[battery.charge_state] ?? 1),
          c_rating_max: num(battery.c_rating_max) || undefined, c_rating_cont: num(battery.c_rating_cont) || undefined,
        },
      };
      // Avionics draw (W → A at nominal pack voltage) rides the accessory rail.
      const packVNominal = num(battery.cells_series, 4) * num(battery.nominal_voltage_v, 3.7);
      const avionicsDrawW = num(fcu.power_draw_w) + sensorsDrawW;
      const avionicsA = packVNominal > 0 ? avionicsDrawW / packVNominal : 0;
      const avionics = [
        ...(fcu.id ? [{ name: "Flight controller", temp_min_c: num(fcu.temp_min_c, null), temp_max_c: num(fcu.temp_max_c, null) }] : []),
        ...selectedSensors.map((s) => ({ name: `${s.brand} ${s.name}`, temp_min_c: s.temp_min_c, temp_max_c: s.temp_max_c })),
      ];
      const opts = {
        all_up_weight_g: num(general.all_up_weight_g), num_motors: num(general.num_motors, 4), coaxial: general.coaxial,
        cells_series: num(battery.cells_series, 4), parallel: num(battery.parallel, 1), max_discharge_pct: num(battery.max_discharge_pct, 85),
        field_elevation_m: num(env.field_elevation_m), air_temperature_c: num(env.temp_max_c, 15), pressure_hpa: num(env.pressure_hpa, 1013),
        temp_min_c: env.temp_min_c === "" ? undefined : num(env.temp_min_c), avionics,
        gear_ratio: num(prop.gear_ratio, 1), blades: num(prop.blades, 2),
        accessory_current_a: num(general.accessory_current_a) + avionicsA,
        frame_size_mm: num(general.frame_size_mm) || undefined, tilt_limit_deg: num(general.tilt_limit_deg) || undefined,
        cooling: motor.cooling, yoke_twist_deg: num(prop.yoke_twist_deg),
        batt_wire_awg: num(esc.batt_wire_awg) || undefined, batt_wire_mm: num(esc.batt_wire_mm),
        motor_wire_awg: num(esc.motor_wire_awg) || undefined, motor_wire_mm: num(esc.motor_wire_mm),
      };
      return { result: simulateModeled(parts, opts), curve: sweepModeled(parts, opts), error: null };
    } catch (e) {
      return { result: null, curve: null, error: e.message };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [general, battery, esc, motor, prop, fcu, sensorIds, env]);

  // Short human label for the current setup (used by pins and share toasts).
  const currentLabel = (() => {
    const m = motorOptions.find((x) => x.id === ids.motor);
    return `${m ? m.name : `${num(motor.kv)}Kv`} · ${num(prop.diameter_in)}×${num(prop.pitch_in)} · ${num(battery.cells_series)}S`;
  })();

  const pinSetup = () => {
    if (!result) return;
    if (pinned.length >= pinLimit) {
      if (!isAuthenticated) {
        requireSignin(); // more comparison slots (and saved setups) behind sign-in
        return;
      }
      enqueueSnackbar(`Comparison holds up to ${pinLimit} pinned setups — remove one first.`, { variant: "info" });
      return;
    }
    setPinned((p) => [
      ...p,
      { id: `pin-${Date.now()}`, label: currentLabel, sys: result.system, curve },
    ]);
    enqueueSnackbar(`Pinned “${currentLabel}” — open the Comparison tab to compare.`, { variant: "success" });
  };

  // Drive weight from part weights (eCalc's "Drive Weight" + avionics):
  // motors + ESCs + pack(s) + flight controller + sensors.
  const driveWeightG = Math.round(
    num(general.num_motors, 0) * (num(motor.weight_g) + num(esc.weight_g)) +
    num(battery.weight_g) * Math.max(1, num(battery.parallel, 1)) +
    num(fcu.weight_g) + sensorsWeightG
  );

  const packV = round2(num(battery.cells_series, 0) * num(battery.nominal_voltage_v, 0));
  const uSx = { flex: "1 1 190px", minWidth: 170 };
  const nSx = { flex: "1 1 120px", minWidth: 110 };
  const selSx = { flex: "1 1 100%", minWidth: 220 };

  const signInAction = !isAuthenticated ? (
    <Box
      onClick={() => requireSignin()}
      sx={{ mt: 2, p: 1.5, textAlign: "center", cursor: "pointer", borderRadius: 2, border: `1px solid ${ACCENT}`, color: ACCENT, fontWeight: 600, "&:hover": { backgroundColor: `${ACCENT}14` } }}
    >
      Sign in to save setups & use verified thrust-stand data
    </Box>
  ) : null;

  return (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      <Box sx={{ maxWidth: 1080, mx: "auto", px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 5 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.text.primary }}>
            Propulsion Calculator
          </Typography>
          <Chip icon={<BoltIcon sx={{ fontSize: 16 }} />} label="Live · in your browser · free" size="small"
            sx={{ backgroundColor: `${ACCENT}14`, color: ACCENT, fontWeight: 600 }} />
        </Box>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mt: 0.5, mb: 1 }}>
          Pick parts to prefill, then fine-tune any spec — everything recalculates instantly. No sign-up.
        </Typography>

        {/* Community actions: contribute + share are open to everyone; comparison
            is the sign-in hook (saved setups + side-by-side + verified data). */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
          <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => setDialog("submit")}
            sx={{ color: ACCENT, textTransform: "none", fontWeight: 600 }}>
            Submit a part
          </Button>
          <Button size="small" startIcon={<ShareOutlinedIcon />} onClick={shareSetup}
            sx={{ color: ACCENT, textTransform: "none", fontWeight: 600 }}>
            Share this setup
          </Button>
          <Button size="small" startIcon={<FlagOutlinedIcon />} onClick={() => setDialog("report")}
            sx={{ color: theme.palette.text.secondary, textTransform: "none", fontWeight: 600 }}>
            Report data
          </Button>
          {isAuthenticated && (
            <Button size="small"
              component={RouterLink}
              to={CLASSIC_PATH}
              sx={{ color: theme.palette.text.secondary, textTransform: "none", fontWeight: 600 }}>
              Classic builder — save combinations
            </Button>
          )}
          <Button size="small" startIcon={<CompareArrowsIcon />}
            onClick={() => (isAuthenticated ? window.location.assign(APP_BASE) : requireSignin())}
            variant="outlined"
            sx={{ ml: "auto", color: ACCENT, borderColor: ACCENT, textTransform: "none", fontWeight: 600,
              "&:hover": { borderColor: ACCENT, backgroundColor: `${ACCENT}14` } }}>
            {isAuthenticated ? "Compare saved setups" : "Compare setups side-by-side — sign in"}
          </Button>
        </Box>

        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{ mt: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
            "& .Mui-selected": { color: `${ACCENT} !important` },
            "& .MuiTabs-indicator": { backgroundColor: ACCENT } }}
        >
          <Tab value="calc" label="Calculator" />
          <Tab value="compare" label={`Comparison${pinned.length ? ` (${pinned.length})` : ""}`} />
        </Tabs>

        {tab === "calc" && (
        <>
        <Box sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.6)}`, px: { xs: 2, md: 3 }, pb: 1, mt: 3 }}>
          {/* Profile quick-start */}
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, py: 2 }}>
            <Typography sx={{ width: { sm: 96 }, flexShrink: 0, fontWeight: 700, display: "flex", alignItems: "center", gap: 0.75, pt: { sm: 1 } }}>
              <FlightTakeoffOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} /> Profile
            </Typography>
            <Box sx={{ flex: 1 }}>
              <TextField sx={{ maxWidth: 360 }} fullWidth size="small" select label="Drone profile" value={droneId} onChange={applyDrone}>
                <MenuItem value=""><em>Custom</em></MenuItem>
                {DEMO_DRONES.map((d) => <MenuItem key={d.id} value={d.id}>{droneLabel(d)}</MenuItem>)}
              </TextField>
            </Box>
          </Box>

          <Section
            label="General" theme={theme} open={open.env} onToggle={toggle("env")} toggleLabel="Conditions"
            primary={
              <>
                <UnitField sx={uSx} label="Model weight" defsKey="weight" value={general.all_up_weight_g} onChange={setG("all_up_weight_g")} />
                <NumField sx={nSx} label="# Motors" value={general.num_motors} onChange={setG("num_motors")} />
                <FormControlLabel sx={{ ml: 0.5 }} control={<Switch checked={general.coaxial} onChange={(e) => setG("coaxial")(e.target.checked)} size="small" />}
                  label={<Typography variant="body2">Coaxial</Typography>} />
              </>
            }
            advanced={
              <>
                <NumField sx={nSx} label="Accessory draw (A)" value={general.accessory_current_a} onChange={setG("accessory_current_a")} />
                <UnitField sx={uSx} label="Frame size" defsKey="lenmm" value={general.frame_size_mm} onChange={setG("frame_size_mm")} />
                <TextField sx={nSx} size="small" select label="FCU tilt limit" value={general.tilt_limit_deg} onChange={(e) => setG("tilt_limit_deg")(e.target.value)}>
                  <MenuItem value=""><em>No limit</em></MenuItem>
                  {[10, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80].map((d) => (
                    <MenuItem key={d} value={d}>{d}°</MenuItem>
                  ))}
                </TextField>
              </>
            }
          />

          <Section
            label="Battery" theme={theme} open={open.bat} onToggle={toggle("bat")}
            primary={
              <>
                <TextField sx={selSx} size="small" select label="Select battery (prefill)" value={ids.battery} onChange={pick("battery")}>
                  {DEMO_CATALOG.batteries.map((b) => <MenuItem key={b.id} value={b.id}>{batLabel(b)}</MenuItem>)}
                </TextField>
                <NumField sx={nSx} label="Cells (S)" value={battery.cells_series} onChange={setB("cells_series")} />
                <NumField sx={nSx} label="Parallel (P)" value={battery.parallel} onChange={setB("parallel")} />
                <TextField sx={nSx} size="small" select label="Charge state" value={battery.charge_state} onChange={(e) => setB("charge_state")(e.target.value)}>
                  <MenuItem value="full">Full (4.2 V)</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="low">Low (3.5 V)</MenuItem>
                </TextField>
                <NumField sx={nSx} label="max discharge %" value={battery.max_discharge_pct} onChange={setB("max_discharge_pct")} />
              </>
            }
            advanced={
              <>
                <NumField sx={nSx} label="Capacity (mAh)" value={battery.capacity_mah} onChange={setB("capacity_mah")} />
                <TextField sx={nSx} size="small" label="Total capacity" value={`${Math.round(num(battery.capacity_mah) * Math.max(1, num(battery.parallel, 1)))} mAh`} InputProps={{ readOnly: true }} disabled />
                <NumField sx={nSx} label="Nominal V/cell" value={battery.nominal_voltage_v} onChange={setB("nominal_voltage_v")} />
                <NumField sx={nSx} label="Resistance (Ω/cell)" value={battery.resistance_ohm} onChange={setB("resistance_ohm")} />
                <NumField sx={nSx} label="C-rate (cont.)" value={battery.c_rating_cont} onChange={setB("c_rating_cont")} />
                <NumField sx={nSx} label="C-rate (max)" value={battery.c_rating_max} onChange={setB("c_rating_max")} />
                <UnitField sx={uSx} label="Pack weight" defsKey="weight" value={battery.weight_g} onChange={setB("weight_g")} />
                <TextField sx={nSx} size="small" label="Pack voltage" value={`${packV} V`} InputProps={{ readOnly: true }} disabled />
              </>
            }
          />

          <Section
            label="ESC" theme={theme} open={open.esc} onToggle={toggle("esc")}
            primary={
              <TextField sx={selSx} size="small" select label="Select ESC (prefill)" value={ids.esc} onChange={pick("esc")}>
                {DEMO_CATALOG.escs.map((x) => <MenuItem key={x.id} value={x.id}>{escLabel(x)}</MenuItem>)}
              </TextField>
            }
            advanced={
              <>
                <NumField sx={nSx} label="Cont. current (A)" value={esc.cont_current_a} onChange={setE("cont_current_a")} />
                <NumField sx={nSx} label="Max current (A)" value={esc.max_current_a} onChange={setE("max_current_a")} />
                <NumField sx={nSx} label="Resistance (Ω)" value={esc.resistance_ohm} onChange={setE("resistance_ohm")} />
                <UnitField sx={uSx} label="Weight" defsKey="weight" value={esc.weight_g} onChange={setE("weight_g")} />
                <TextField sx={nSx} size="small" select label="Battery wire" value={esc.batt_wire_awg} onChange={(e) => setE("batt_wire_awg")(e.target.value)}>
                  {[8, 10, 12, 14, 16, 18, 20].map((awg) => <MenuItem key={awg} value={awg}>AWG{awg}</MenuItem>)}
                </TextField>
                <NumField sx={nSx} label="Length (mm)" value={esc.batt_wire_mm} onChange={setE("batt_wire_mm")} />
                <TextField sx={nSx} size="small" select label="Motor wire" value={esc.motor_wire_awg} onChange={(e) => setE("motor_wire_awg")(e.target.value)}>
                  {[8, 10, 12, 14, 16, 18, 20].map((awg) => <MenuItem key={awg} value={awg}>AWG{awg}</MenuItem>)}
                </TextField>
                <NumField sx={nSx} label="Length (mm)" value={esc.motor_wire_mm} onChange={setE("motor_wire_mm")} />
              </>
            }
          />

          <Section
            label="Motor" theme={theme} open={open.mot} onToggle={toggle("mot")}
            primary={
              <TextField sx={selSx} size="small" select label="Select motor (prefill)" value={ids.motor} onChange={pick("motor")}>
                {motorOptions.map((m) => <MenuItem key={m.id} value={m.id}>{motorLabel(m)}</MenuItem>)}
              </TextField>
            }
            advanced={
              <>
                <NumField sx={nSx} label="Kv (rpm/V)" value={motor.kv} onChange={setM("kv")} />
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flex: "1 1 220px", minWidth: 200 }}>
                  <NumField sx={{ flex: 1 }} label="No-load current (A)" value={motor.no_load_current_a} onChange={setM("no_load_current_a")} />
                  <Typography sx={{ color: theme.palette.text.secondary }}>@</Typography>
                  <NumField sx={{ width: 72 }} label="V" value={motor.no_load_voltage_v} onChange={setM("no_load_voltage_v")} />
                </Box>
                <NumField sx={nSx} label="Limit (W)" value={motor.max_power_w} onChange={setM("max_power_w")} />
                <NumField sx={nSx} label="Resistance (Ω)" value={motor.resistance_ohm} onChange={setM("resistance_ohm")} />
                <NumField sx={nSx} label="Max current (A)" value={motor.max_current_a} onChange={setM("max_current_a")} />
                <UnitField sx={uSx} label="Case length" defsKey="lenmm" value={motor.case_length_mm} onChange={setM("case_length_mm")} />
                <NumField sx={nSx} label="# mag. poles" value={motor.poles} onChange={setM("poles")} />
                <UnitField sx={uSx} label="Weight" defsKey="weight" value={motor.weight_g} onChange={setM("weight_g")} />
                <TextField sx={nSx} size="small" select label="Cooling" value={motor.cooling} onChange={(e) => setM("cooling")(e.target.value)}>
                  {["excellent", "good", "medium", "poor", "very poor"].map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </TextField>
              </>
            }
          />

          <Section
            label="Propeller" theme={theme} open={open.prop} onToggle={toggle("prop")}
            primary={
              <>
                <TextField sx={selSx} size="small" select label="Select propeller (prefill)" value={ids.propeller} onChange={pick("propeller")}>
                  {DEMO_CATALOG.propellers.map((p) => <MenuItem key={p.id} value={p.id}>{propLabel(p)}</MenuItem>)}
                </TextField>
                <UnitField sx={uSx} label="Diameter" defsKey="len" value={prop.diameter_in} onChange={setP("diameter_in")} />
                <UnitField sx={uSx} label="Pitch" defsKey="len" value={prop.pitch_in} onChange={setP("pitch_in")} />
                <NumField sx={nSx} label="# Blades" value={prop.blades} onChange={setP("blades")} />
              </>
            }
            advanced={
              <>
                <NumField sx={nSx} label="Thrust const" value={prop.t_const} onChange={setP("t_const")} />
                <NumField sx={nSx} label="Power const" value={prop.p_const} onChange={setP("p_const")} />
                <NumField sx={nSx} label="Gear ratio (:1)" value={prop.gear_ratio} onChange={setP("gear_ratio")} />
                <TextField sx={nSx} size="small" select label="Yoke twist" value={prop.yoke_twist_deg} onChange={(e) => setP("yoke_twist_deg")(e.target.value)}>
                  {[7, 6, 5, 4, 3.5, 3, 2.5, 2, 0, -2.5, -5, -7].map((d) => (
                    <MenuItem key={d} value={d}>{d > 0 ? `+${d}°` : `${d}°`}</MenuItem>
                  ))}
                </TextField>
              </>
            }
          />

          <Section
            label="Flight ctrl" theme={theme} open={open.fcu} onToggle={toggle("fcu")} toggleLabel="Temp range"
            primary={
              <>
                <TextField sx={{ flex: "1 1 260px", minWidth: 220 }} size="small" select label="Select flight controller" value={fcu.id} onChange={pickFcu}>
                  <MenuItem value=""><em>None / custom</em></MenuItem>
                  {DEMO_CATALOG.fcus.map((f) => (
                    <MenuItem key={f.id} value={f.id}>{`${f.brand} ${f.name} · ${f.power_draw_w} W · ${f.weight_g} g`}</MenuItem>
                  ))}
                </TextField>
                <NumField sx={nSx} label="Power draw (W)" value={fcu.power_draw_w} onChange={setFc("power_draw_w")} />
                <UnitField sx={uSx} label="Weight" defsKey="weight" value={fcu.weight_g} onChange={setFc("weight_g")} />
              </>
            }
            advanced={
              <>
                <NumField sx={nSx} label="Op. temp min (°C)" value={fcu.temp_min_c} onChange={setFc("temp_min_c")} />
                <NumField sx={nSx} label="Op. temp max (°C)" value={fcu.temp_max_c} onChange={setFc("temp_max_c")} />
              </>
            }
          />

          <Section
            label="Sensors" theme={theme}
            primary={
              <>
                <TextField
                  sx={{ flex: "1 1 300px", minWidth: 260 }} size="small" select label="Select sensors (multiple)"
                  value={sensorIds}
                  onChange={(e) => setSensorIds(e.target.value)}
                  SelectProps={{
                    multiple: true,
                    renderValue: (v) =>
                      v.map((id) => DEMO_CATALOG.sensors.find((s) => s.id === id)?.name || id).join(", "),
                  }}
                >
                  {DEMO_CATALOG.sensors.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox size="small" checked={sensorIds.includes(s.id)} />
                      <ListItemText primary={`${s.brand} ${s.name}`} secondary={`${s.sensor_type} · ${s.power_draw_w} W · ${s.weight_g} g · ${s.temp_min_c}…${s.temp_max_c}°C`} />
                    </MenuItem>
                  ))}
                </TextField>
                <Chip size="small" variant="outlined" sx={{ alignSelf: "center", color: theme.palette.text.secondary }}
                  label={`Σ ${Math.round((num(fcu.power_draw_w) + sensorsDrawW) * 10) / 10} W avionics · ${Math.round(num(fcu.weight_g) + sensorsWeightG)} g`} />
              </>
            }
          />

          <Section
            label="Operating env" theme={theme}
            primary={
              <>
                <NumField sx={nSx} label="Temp min (°C)" value={env.temp_min_c} onChange={setEnvF("temp_min_c")} />
                <NumField sx={nSx} label="Temp max (°C)" value={env.temp_max_c} onChange={setEnvF("temp_max_c")} />
                <UnitField sx={uSx} label="Field elevation" defsKey="elev" value={env.field_elevation_m} onChange={setEnvF("field_elevation_m")} />
                <UnitField sx={uSx} label="Pressure (QNH)" defsKey="press" value={env.pressure_hpa} onChange={setEnvF("pressure_hpa")} />
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, alignSelf: "center", flexBasis: "100%" }}>
                  Thrust is computed at the hot end (worst air density); battery capacity is derated at the cold
                  end; avionics operating ranges are checked against both.
                </Typography>
              </>
            }
          />
        </Box>

        {driveWeightG > 0 && (
          <Typography
            variant="caption"
            sx={{
              display: "block", mt: 1, color:
                driveWeightG > num(general.all_up_weight_g) ? "#e5484d" : theme.palette.text.secondary,
            }}
          >
            Drive weight (motors + ESCs + pack) ≈ {driveWeightG} g
            {num(general.all_up_weight_g) > 0 &&
              ` · ${Math.round((driveWeightG / num(general.all_up_weight_g)) * 100)}% of all-up weight`}
            {driveWeightG > num(general.all_up_weight_g) && " — heavier than the model weight; check your inputs"}
          </Typography>
        )}

        {/* Live results */}
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, letterSpacing: 0.4 }}>RESULTS</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {result && (
                <Button size="small" startIcon={<CompareArrowsIcon />} onClick={pinSetup}
                  sx={{ color: ACCENT, textTransform: "none", fontWeight: 600 }}>
                  Pin to compare{pinned.length > 0 ? ` (${pinned.length}/${pinLimit})` : ""}
                </Button>
              )}
              {result && <Chip label="Updates live" size="small" variant="outlined" sx={{ color: theme.palette.text.secondary }} />}
            </Box>
          </Box>
          {error ? (
            <Alert severity="error">{error}</Alert>
          ) : !result ? (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Enter a valid setup to calculate.</Typography>
          ) : (
            <ResultsView result={result} curve={curve} action={signInAction} chartNote="Modeled characteristic (±~15%), computed in your browser" />
          )}
        </Box>
        </>
        )}

        {tab === "compare" && (
          <Box sx={{ mt: 3 }}>
            {/* Save the current pin set as a named comparison */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
              <TextField size="small" label="Comparison name" value={compName}
                onChange={(e) => setCompName(e.target.value)} sx={{ flex: "1 1 240px", maxWidth: 340 }} />
              <Button size="small" variant="outlined" startIcon={<BookmarkAddOutlinedIcon />}
                onClick={saveComparison} disabled={!pinned.length}
                sx={{ color: ACCENT, borderColor: ACCENT, textTransform: "none", fontWeight: 600,
                  "&:hover": { borderColor: ACCENT, backgroundColor: `${ACCENT}14` } }}>
                Save comparison
              </Button>
              <Chip size="small" variant="outlined"
                label={compsSource === "account" ? "Synced to your account" : "Stored in this browser"}
                sx={{ color: theme.palette.text.secondary }} />
              {!pinned.length && (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Pin setups from the Calculator tab to compare them here.
                </Typography>
              )}
            </Box>

            {/* Saved sets */}
            {savedComps.length > 0 && (
              <Box sx={{ mb: 3, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}>
                {savedComps.map((c, i) => (
                  <Box key={c.id}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1,
                      borderTop: i ? `1px solid ${alpha(theme.palette.divider, 0.4)}` : "none" }}>
                    <Typography sx={{ fontWeight: 600, flex: 1, minWidth: 0, color: theme.palette.text.primary }} noWrap>{c.name}</Typography>
                    <Chip size="small" variant="outlined" label={`${c.entries.length} setups · ${comparisonDate(c)}`}
                      sx={{ color: theme.palette.text.secondary }} />
                    <Button size="small" onClick={() => loadComparison(c)}
                      sx={{ color: ACCENT, textTransform: "none", fontWeight: 600 }}>
                      Load
                    </Button>
                    <IconButton size="small" aria-label="Delete comparison" onClick={() => deleteSavedComp(c.id)}>
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {result && (
              <ComparisonTray
                entries={pinned}
                current={{ label: currentLabel, sys: result.system, curve }}
                onRemove={(id) => setPinned((p) => p.filter((x) => x.id !== id))}
              />
            )}
          </Box>
        )}

        <Divider sx={{ my: 4 }} />
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          Not yet modeled (needs a forward-flight drag model): flight speed &amp; range. Motor temperature is a
          first-order estimate from waste power and cooling grade — treat it as a sanity check, not a datasheet.
        </Typography>
      </Box>

      <SubmitPartDialog open={dialog === "submit"} onClose={() => setDialog(null)} />
      <ReportDataDialog open={dialog === "report"} onClose={() => setDialog(null)} />
    </Box>
  );
};

export default PublicPropulsion;
